import { Stack, xcss, Box } from '@atlaskit/primitives';
import { useDatabaseContent } from '@databases/database-state';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { getDatabaseFields } from '@databases/utils/database/utils';
import { getViewById } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { FilterTypePicker } from '@databases/view-settings';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import useDeleteView from '../../hooks/useDeleteView';
import { useSaveAsNewView } from '../../hooks/useSaveAsNewView';
import { useUpdateView } from '../../hooks/useUpdateView';
import useView from '../../hooks/useView';
import { useViewHasUnsavedChanges } from '../../hooks/useViewHasUnsavedChanges';
import { useViewUiState } from '../../hooks/useViewUiState';
import { ViewPanelSettingType } from '../../types/types';
import { isUniqueViewName } from '../../utils/viewName';
import ViewSettingsFields from '../viewSettingsFields/ViewSettingsFields';
import ViewSettingsFilter from '../viewSettingsFilter/ViewSettingsFilter';
import ViewSettingsFooter from '../viewSettingsFooter/ViewSettingsFooter';
import ViewSettingsHeader from '../viewSettingsHeader/ViewSettingsHeader';
import ViewSettingsLayout from '../viewSettingsLayout/ViewSettingsLayout';
import ViewSettingsOverview from '../viewSettingsOverview/ViewSettingsOverview';
import ViewSettingsSort from '../viewSettingsSort/ViewSettingsSort';

const stackStyles = xcss({
  width: '100%',
  height: '100%',
});

const bodyBoxStyles = xcss({
  padding: 'space.300',
});

export interface ViewSettingsProps {
  FieldPicker: React.FunctionComponent<any>;
  FilterValueEdit: React.FunctionComponent<any>;
}

const ViewSettings: FunctionComponent<ViewSettingsProps> = (props) => {
  const { contentId } = useDatabaseContent();

  if (!contentId) {
    return null;
  }

  return <ViewSettingsInternal {...props} contentId={contentId} />;
};

const ViewSettingsInternal: FunctionComponent<
  ViewSettingsProps & { contentId: string }
> = ({ FieldPicker, FilterValueEdit, contentId }) => {
  const [viewNameIsValid, setViewNameIsValid] = useState<boolean>(true);
  const { view } = useView();
  const { activePanelSetting, setActivePanelSetting } = useViewUiState();
  const { hidePanel, getData, setUnsavedChanges, isPanelVisible } = usePanel();
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null);

  const [viewName, setViewName] = useState(view.name);

  const { autoFocusLastFilterValue, canEditViews } = getData<'ViewSettingsPanel'>();
  const handleDeleteView = useDeleteView(contentId);

  const [totalViews, viewNames, databaseFields, savedViews] =
    defaultStoreUtils.useEqualSelector((state) => {
      const database = selectDatabaseByContentIdRequired(state, { contentId });
      const filteredViews = database.views.filter((v) => v.id !== view.id);
      return [
        database.views.length,
        filteredViews.map((view) => view.name.trim().toLowerCase()),
        getDatabaseFields(database),
        database.views,
      ];
    });

  const originalView = useMemo(
    () => getViewById(savedViews, view.id),
    [savedViews, view.id]
  );

  const hasUnsavedSettingsChanges = useViewHasUnsavedChanges({ contentId });

  const hasNameChanges = useMemo(() => {
    return (
      (originalView && isUniqueViewName(viewName, [...viewNames, originalView.name])) ??
      false
    );
  }, [originalView, viewName, viewNames]);

  const hasChanges = hasUnsavedSettingsChanges || hasNameChanges;

  useEffect(() => {
    setUnsavedChanges('ViewSettingsPanel', hasChanges);
  }, [hasChanges, setUnsavedChanges]);

  useEffect(() => {
    setViewNameIsValid(
      viewName.trim() !== '' && !viewNames.includes(viewName.trim().toLowerCase())
    );
  }, [originalView, viewNameIsValid, viewNames, viewName]);

  const getTriggerButton = useCallback(
    (setting: ViewPanelSettingType | null): HTMLElement | null => {
      switch (setting) {
        case ViewPanelSettingType.FILTER:
          return document.querySelector('#view-settings-filter-button');
        case ViewPanelSettingType.LAYOUT:
          return document.querySelector('#view-settings-layout-button');
        case ViewPanelSettingType.SORT:
          return document.querySelector('#view-settings-sort-button');
        case ViewPanelSettingType.FIELDS:
          return document.querySelector('#view-settings-hide-field-button');
        case ViewPanelSettingType.OVERVIEW:
          return document.querySelector('.view-actions-menu-trigger-button');
        default:
          return null;
      }
    },
    []
  );

  useEffect(() => {
    if (isPanelVisible('ViewSettingsPanel')) {
      // Store the triggering element when panel opens
      setTriggerElement(getTriggerButton(activePanelSetting));
    } else {
      setActivePanelSetting(null);
    }
  }, [isPanelVisible, setActivePanelSetting, activePanelSetting, getTriggerButton]);

  const onClose = useCallback(() => {
    hidePanel({ ignoreUnsavedChanges: !canEditViews });
    // Return focus to the triggering element
    setTimeout(() => {
      if (triggerElement) {
        triggerElement.focus();
      }
    }, 0);
  }, [canEditViews, hidePanel, triggerElement]);

  const newViewName = viewName !== originalView?.name ? viewName : undefined;

  const saveAsNewView = useSaveAsNewView({ contentId });

  const handleSaveAsNewView = useCallback(() => {
    saveAsNewView({ analyticsSource: 'databaseViewSettingsPanel', newViewName });
    setActivePanelSetting(ViewPanelSettingType.OVERVIEW);
  }, [newViewName, saveAsNewView, setActivePanelSetting]);

  const updateView = useUpdateView({ contentId });

  const handleViewUpdate = useCallback(async () => {
    await updateView({
      analyticsSource: 'databaseViewSettingsPanel',
      newViewName,
    });
    setUnsavedChanges('ViewSettingsPanel', false);
    hidePanel({ ignoreUnsavedChanges: true });
  }, [hidePanel, newViewName, setUnsavedChanges, updateView]);

  const onBack = useCallback(() => {
    setActivePanelSetting(ViewPanelSettingType.OVERVIEW);
  }, [setActivePanelSetting]);

  const handleDeleteViewWithClose = useCallback(() => {
    handleDeleteView(view.id, 'databaseViewSettingsPanel', onClose);
  }, [handleDeleteView, onClose, view]);

  const isOverview = activePanelSetting === ViewPanelSettingType.OVERVIEW;

  const ViewSettingsBody = useCallback(() => {
    switch (activePanelSetting) {
      case ViewPanelSettingType.LAYOUT:
        return <ViewSettingsLayout contentId={contentId} FieldPicker={FieldPicker} />;
      case ViewPanelSettingType.FILTER:
        return (
          <ViewSettingsFilter
            contentId={contentId}
            FieldPicker={FieldPicker}
            FilterTypePicker={FilterTypePicker}
            FilterValueEdit={FilterValueEdit}
            autoFocusLastFilterValue={autoFocusLastFilterValue}
          />
        );
      case ViewPanelSettingType.SORT:
        return <ViewSettingsSort contentId={contentId} databaseFields={databaseFields} />;
      case ViewPanelSettingType.FIELDS:
        return (
          <ViewSettingsFields contentId={contentId} databaseFields={databaseFields} />
        );
    }
  }, [
    activePanelSetting,
    contentId,
    FieldPicker,
    FilterValueEdit,
    autoFocusLastFilterValue,
    databaseFields,
  ]);

  useEffect(() => {
    setViewName(view.name);
  }, [view.name, setViewName]);

  return (
    <Stack xcss={stackStyles} spread="space-between" testId="view-settings-panel-content">
      <Box>
        <ViewSettingsHeader
          contentId={contentId}
          onDelete={handleDeleteViewWithClose}
          onClose={onClose}
          hasChanges={hasChanges}
          onBack={onBack}
          isOverview={isOverview}
          canEditViews={canEditViews}
          totalViews={totalViews}
        />
        <Box xcss={bodyBoxStyles}>
          {/* Overview cannot go in callback hook since it causes re-mounting on prop change
              which causes rename to re-mount */}
          {activePanelSetting === ViewPanelSettingType.OVERVIEW ? (
            <ViewSettingsOverview
              viewNameIsValid={viewNameIsValid}
              viewNames={viewNames}
              viewName={viewName}
              setViewName={setViewName}
              canEditViews={canEditViews}
            />
          ) : (
            <ViewSettingsBody />
          )}
        </Box>
      </Box>

      <ViewSettingsFooter
        contentId={contentId}
        handleViewUpdate={handleViewUpdate}
        handleSaveAsNewView={handleSaveAsNewView}
        viewNameIsValid={viewNameIsValid}
        hasChanges={hasChanges}
        canEditViews={canEditViews}
      />
    </Stack>
  );
};

export default ViewSettings;
